import { useCallback, useMemo, useState } from 'react';

import { useIsFetching } from '@tanstack/react-query';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { reservationQueryKeys } from '~/hooks/apis/reservations/reservationQueryKeys';
import {
  type ExtraGuestSurchargeWrite,
  useApplyExtraGuestSurcharges,
} from '~/hooks/apis/reservations/useApplyExtraGuestSurcharges';

import presentFXRates from '~/utils/presentFXRates';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import {
  EMPTY_GATE_RESOLUTION,
  type EgsArchiveDecision,
  type EgsGateResolution,
  buildEgsApplyPlans,
  buildEgsPayload,
  getEgsFxRatesBlocker,
  getNothingToApplyReasonAcrossCurrencies,
  isApplicable,
  isWaitingOnFxRates,
  isWaitingOnGate,
} from './buildEgsApplyPlan';
import { buildDefaultSelectedRoomRateIds, buildEgsApplyCandidates } from './egsApplyCandidates';
import { buildEgsRatePlans, filterSelectionsByRatePlan, getDefaultEgsRatePlanId } from './egsRatePlans';
import type { EgsRoomMatch } from './egsRoomMatcher';
import type { EgsWriteCurrencyMode } from './egsWriteCurrency';
import type { EgsGuestSlot, EgsRoomGroup } from './types';

export function useEgsApplyPlan({
  egsByRoomRateId,
  matchByRoomLabel,
  onApplied,
  propertyId,
  roomGroups,
  roomRateSelections,
}: {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  matchByRoomLabel: Record<string, EgsRoomMatch>;
  onApplied: () => void;
  propertyId: string;
  roomGroups: Array<EgsRoomGroup>;
  roomRateSelections: Array<RoomRateSelection>;
}) {
  const applyExtraGuestSurcharges = useApplyExtraGuestSurcharges(propertyId);

  // The same `/api/fx/rates` svc-reservation syncs, so a stored amount re-converts at quote time stably.
  const { data: fxRates, status: fxRatesStatus } = useGetFxRates();
  const fxRateLookup = useMemo(() => presentFXRates(fxRates ?? []), [fxRates]);

  /**
   * Until the invalidated records refetch, a room rate that was just created still reads as `create` and
   * re-sending would write a second record, so the reload counts as still applying.
   */
  const isReloadingExistingRecords = useIsFetching({
    queryKey: reservationQueryKeys.extraGuestSurchargesByRoomRate(propertyId, []),
  });

  const ratePlans = useMemo(() => buildEgsRatePlans(roomRateSelections), [roomRateSelections]);

  const [ratePlanId, setRatePlanId] = useState<string | undefined>(() => getDefaultEgsRatePlanId(ratePlans));

  const scopedRoomRateSelections = useMemo(
    () => filterSelectionsByRatePlan(roomRateSelections, ratePlanId),
    [ratePlanId, roomRateSelections],
  );

  const buildCandidatesFor = useCallback(
    (selections: Array<RoomRateSelection>) =>
      buildEgsApplyCandidates({ matchByRoomLabel, roomGroups, roomRateSelections: selections }),
    [matchByRoomLabel, roomGroups],
  );

  const candidates = useMemo(
    () => buildCandidatesFor(scopedRoomRateSelections),
    [buildCandidatesFor, scopedRoomRateSelections],
  );

  // Seeded once rather than synced, so re-deriving candidates cannot discard ticks the CM already made.
  const [selectedRoomRateIdsByRoomLabel, setSelectedRoomRateIdsByRoomLabel] = useState<Record<string, Array<string>>>(
    () => buildDefaultSelectedRoomRateIds(candidates),
  );

  const selectionsByRoomLabel = useMemo(() => {
    return Object.fromEntries(
      candidates.map((candidate) => {
        const selectedIds = new Set(selectedRoomRateIdsByRoomLabel[candidate.roomLabel] ?? []);

        return [
          candidate.roomLabel,
          candidate.candidates.filter((selection) => selectedIds.has(String(selection.roomRate.id))),
        ];
      }),
    );
  }, [candidates, selectedRoomRateIdsByRoomLabel]);

  const [archiveByRowId, setArchiveByRowId] = useState<Record<string, EgsArchiveDecision>>({});
  const [gateByRoomRateId, setGateByRoomRateId] = useState<Record<string, EgsGateResolution>>({});
  const [hasDiscardedGateAnswers, setHasDiscardedGateAnswers] = useState(false);
  // Kept out of `gateByRoomRateId`, whose clearing raises the discarded-answers notice. This answers no gate.
  const [writeCurrencyModeByRoomRateId, setWriteCurrencyModeByRoomRateId] = useState<
    Record<string, EgsWriteCurrencyMode>
  >({});

  /**
   * An answer names two specific rows, so a decision that outlives the mapping it was given for waves through
   * a conflict nobody looked at. Called from the setters that move the mapping rather than an effect watching
   * it, so no render survives between the tick and the clear.
   */
  const clearGateAnswers = () => {
    if (Object.keys(archiveByRowId).length > 0 || Object.keys(gateByRoomRateId).length > 0) {
      setHasDiscardedGateAnswers(true);
    }

    setArchiveByRowId({});
    setGateByRoomRateId({});
  };

  const plans = useMemo(
    () =>
      buildEgsApplyPlans({
        archiveByRowId,
        egsByRoomRateId,
        fxRateLookup,
        fxRatesStatus,
        gateByRoomRateId,
        roomGroups,
        selectionsByRoomLabel,
        writeCurrencyModeByRoomRateId,
      }),
    [
      archiveByRowId,
      egsByRoomRateId,
      fxRateLookup,
      fxRatesStatus,
      gateByRoomRateId,
      roomGroups,
      selectionsByRoomLabel,
      writeCurrencyModeByRoomRateId,
    ],
  );

  // Built at full reach, since asking a CM to tick rooms is only honest if some answer writes.
  const nothingToApplyReason = useMemo(() => {
    const plansFor = (defaultWriteCurrencyMode: EgsWriteCurrencyMode) =>
      buildEgsApplyPlans({
        defaultWriteCurrencyMode,
        egsByRoomRateId,
        fxRateLookup,
        fxRatesStatus,
        roomGroups,
        selectionsByRoomLabel: Object.fromEntries(
          candidates.map((candidate) => [candidate.roomLabel, candidate.candidates]),
        ),
      });

    return getNothingToApplyReasonAcrossCurrencies({ aud: plansFor('aud'), contracted: plansFor('contracted') });
  }, [candidates, egsByRoomRateId, fxRateLookup, fxRatesStatus, roomGroups]);

  const applicablePlans = useMemo(() => plans.filter(isApplicable), [plans]);
  // Rows waiting on the rates are left out: they are not skipped, and they show nothing for the CM to fix.
  const blockedPlans = useMemo(
    () => plans.filter((plan) => !!plan.blocker && !isWaitingOnGate(plan) && !isWaitingOnFxRates(plan)),
    [plans],
  );
  const gatedPlans = useMemo(() => plans.filter(isWaitingOnGate), [plans]);
  const fxRatesBlocker = getEgsFxRatesBlocker(plans);

  /**
   * The snackbar names one failure and then goes, so the last attempt's failures stay readable here for as
   * long as the CM is still in the wizard. Firing the mutation again clears `data`, so a retry empties this
   * on its own rather than showing the previous attempt's list beside an in-flight one.
   */
  const failedWrites = useMemo(
    () => (applyExtraGuestSurcharges.data ?? []).filter((result) => !result.succeeded),
    [applyExtraGuestSurcharges.data],
  );

  const toggleRoomRate = (roomLabel: string, roomRateId: string, checked: boolean) => {
    setSelectedRoomRateIdsByRoomLabel((current) => {
      const selectedIds = new Set(current[roomLabel] ?? []);

      if (checked) {
        selectedIds.add(roomRateId);
      } else {
        selectedIds.delete(roomRateId);
      }

      return { ...current, [roomLabel]: Array.from(selectedIds) };
    });
    clearGateAnswers();
  };

  // A tick on one plan names a room rate the next does not have, so ticks are seeded fresh, never carried.
  const selectRatePlan = (nextRatePlanId: string) => {
    if (nextRatePlanId === ratePlanId) {
      return;
    }

    setRatePlanId(nextRatePlanId);
    setSelectedRoomRateIdsByRoomLabel(
      buildDefaultSelectedRoomRateIds(
        buildCandidatesFor(filterSelectionsByRatePlan(roomRateSelections, nextRatePlanId)),
      ),
    );
    clearGateAnswers();
    // Here and not in `clearGateAnswers`: ticking a room keeps the same room rate ids, so the choice still fits.
    setWriteCurrencyModeByRoomRateId({});
  };

  // Answering again is what makes the notice stale, so each of the three setters retires it.
  const setArchiveDecision = (rowId: string, decision: EgsArchiveDecision) => {
    setHasDiscardedGateAnswers(false);
    setArchiveByRowId((current) => ({ ...current, [rowId]: decision }));
  };

  const setSlotWinner = (roomRateId: string, slot: EgsGuestSlot, rowId: string) => {
    setHasDiscardedGateAnswers(false);
    setGateByRoomRateId((current) => {
      const resolution = current[roomRateId] ?? EMPTY_GATE_RESOLUTION;

      return {
        ...current,
        [roomRateId]: { ...resolution, slotWinnerRowIds: { ...resolution.slotWinnerRowIds, [slot]: rowId } },
      };
    });
  };

  const setPayableAtProperty = (roomRateId: string, payableAtProperty: boolean) => {
    setHasDiscardedGateAnswers(false);
    setGateByRoomRateId((current) => ({
      ...current,
      [roomRateId]: { ...EMPTY_GATE_RESOLUTION, ...current[roomRateId], payableAtProperty },
    }));
  };

  const setWriteCurrencyMode = (roomRateId: string, mode: EgsWriteCurrencyMode) => {
    setWriteCurrencyModeByRoomRateId((current) => ({ ...current, [roomRateId]: mode }));
  };

  const handleApply = () => {
    const writes: Array<ExtraGuestSurchargeWrite> = applicablePlans.map((plan) => ({
      operation: plan.operation,
      payload: buildEgsPayload(plan),
      // The room rate is what failed, so name it the way the CM sees it rather than by Salesforce record.
      roomLabel: `${plan.roomTypeName} · ${plan.ratePlanName}`,
      roomRateId: plan.roomRateId,
      roomTypeId: plan.roomTypeId,
    }));

    // Stays open when anything failed, so a retry is targeted: once the invalidated query refetches, the
    // room rates that saved read as unchanged and drop out, leaving only the failures to send again.
    applyExtraGuestSurcharges.mutate(writes, {
      onSuccess: (results) => {
        if (results.every((result) => result.succeeded)) {
          onApplied();
        }
      },
    });
  };

  return {
    applicablePlans,
    blockedPlans,
    candidates,
    failedWrites,
    fxRatesBlocker,
    gatedPlans,
    handleApply,
    hasDiscardedGateAnswers,
    isApplying: applyExtraGuestSurcharges.isPending || isReloadingExistingRecords > 0,
    nothingToApplyReason,
    plans,
    ratePlanId,
    ratePlans,
    selectRatePlan,
    selectedRoomRateIdsByRoomLabel,
    setArchiveDecision,
    setPayableAtProperty,
    setSlotWinner,
    setWriteCurrencyMode,
    toggleRoomRate,
  };
}
